import { parseNumber } from '../../../utils/numberConversions'
import { FORM_STATES } from '../reducer'
import { track } from '@utils/tracking'
import { getAgeFromYear } from '@utils/dateParser'
import { R } from '@utils/currency'

export const formSubmitHandler = async (formAttributes, context) => {
  const { state } = context
  const { formState } = state

  if (formState === FORM_STATES.NEW) {
    await addNewCashflowEvent(formAttributes, context)
  } else {
    await updateCashflowEvent(formAttributes, context)
  }
}

const addNewCashflowEvent = async (formAttributes, context) => {
  const { trackingDetails, props } = context
  const { crudActions } = props
  trackCashflowEvent('Cashflow events: New', trackingDetails, formAttributes)

  const newCashflowEvent = buildCashflowEvent(formAttributes)

  await crudActions.create(newCashflowEvent)
}

const updateCashflowEvent = async (formAttributes, context) => {
  const { state, trackingDetails } = context
  const {
    cashflowEvents,
    eventAttributes,
    crudActions
  } = state
  trackCashflowEvent('Cashflow events: Update', trackingDetails, formAttributes)

  let updatedCashflowEvents = [...cashflowEvents]

  const updatedCashflowEvent = buildCashflowEvent(formAttributes)

  updatedCashflowEvents[eventAttributes.index] = updatedCashflowEvent

  await crudActions.update(updatedCashflowEvent)
}

export const toggleCashflowEvent = async (toggledEvent, context) => {
  const { state, trackingDetails } = context
  const {
    cashflowEvents,
    crudActions
  } = state
  trackCashflowEvent('Cashflow events: Toggle', trackingDetails, toggledEvent)

  let updatedCashflowEvents = [...cashflowEvents]

  updatedCashflowEvents[toggledEvent.index] = {
    ...toggledEvent,
    status: toggledEvent.status === 'active' ? 'inactive' : 'active'
  }

  if (toggledEvent.joint) {
    const joint_cashflow_event_distributions_attributes = toggledEvent.joint_cashflow_event_distributions_attributes
    await crudActions.update({
      id: toggledEvent.id,
      joint_cashflow_event_distributions_attributes: joint_cashflow_event_distributions_attributes
        .map((distribution) => ({
          id: distribution.id,
          cashflow_event_attributes: {
            id: distribution.cashflow_event_attributes.id,
            status: toggledEvent.status === 'active' ? 'inactive' : 'active'
          }
        }))
    })
  } else {
    await crudActions.update({
      id: toggledEvent.id,
      status: toggledEvent.status === 'active' ? 'inactive' : 'active'
    })
  }
}

export const deleteCashflowEvent = async (deletedEvent, context) => {
  const { state, trackingDetails } = context
  const { formState, crudActions } = state

  if (formState === FORM_STATES.EDIT) {
    trackCashflowEvent('Cashflow events: Delete', trackingDetails, deletedEvent)

    await crudActions.delete(deletedEvent.id)
  }
}

const buildCashflowEvent = (formAttributes) => {

  if (formAttributes.joint) {
    return buildJointCashflowEvent(formAttributes)
  } else {
    return buildIndividualCashflowEvent(formAttributes)
  }
}

const buildIndividualCashflowEvent = (formAttributes) => ({
  id: formAttributes.id,
  name: formAttributes.name,
  amount: R(formAttributes.amount).value,
  event_type: formAttributes.event_type,
  status: formAttributes.status || 'active',
  start_age: parseNumber(formAttributes.start_age),
  end_age: parseNumber(formAttributes.end_age),
  frequency: parseNumber(formAttributes.frequency),
  annual_inflation: formAttributes.apply_annual_inflation ? parseNumber(formAttributes.annual_inflation) / 100 : null
})

const buildJointCashflowEvent = (formAttributes) => {
  const joint_cashflow_event_distributions_attributes = formAttributes.joint_cashflow_event_distributions_attributes
  return {
    id: formAttributes.id,
    amount: R(formAttributes.amount).value,
    distribution_type: formAttributes.distribution_type || 'custom',
    joint_cashflow_event_distributions_attributes: joint_cashflow_event_distributions_attributes
      .map((distribution) => ({
        ...distribution,
        value: parseFloat(distribution?.value / 100),
        cashflow_event_attributes: {
          ...distribution?.cashflow_event_attributes,
          name: formAttributes.name,
          amount: parseNumber(formAttributes.amount),
          event_type: formAttributes.event_type,
          status: formAttributes.status || 'active',
          start_age: getAgeFromYear(formAttributes.start_year, distribution.current_age),
          end_age: getAgeFromYear(formAttributes.end_year, distribution.current_age),
          frequency: parseNumber(formAttributes.frequency),
          annual_inflation: formAttributes.apply_annual_inflation ? parseNumber(formAttributes.annual_inflation) / 100 : null
        }
      }))
  }
}

const trackCashflowEvent = (event, trackingDetails, eventAttributes) => {
  const cashflowTrackingDetails = {
    ...trackingDetails,
    recurringEvent: eventAttributes.start_age !== eventAttributes.end_age
  }
  track(event, cashflowTrackingDetails)
}
